import { getApiErrorMessage } from '@quoruum/frontend-common/api/errorMessage';
import { hotelGetStatusOptions } from '@quoruum/frontend-common/oas/gen/admin/@tanstack/react-query.gen';
import type { HotelStatusBean } from '@quoruum/frontend-common/oas/gen/admin/types.gen';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement, ReactNode } from 'react';
import { useMemo } from 'react';
import { EditableHotelFieldKey, formatUnknownValue } from './hotelMapShared';

type DetailRow = {
  id?: string;
  key: string;
  value: ReactNode;
  editableFieldKey?: EditableHotelFieldKey;
  editableFieldValue?: string;
};

function buildStatusRows(status: HotelStatusBean | null | undefined): Array<DetailRow> {
  if (!status) {
    return [
      {
        key: 'value',
        value: '-',
      },
    ];
  }

  return Object.entries(status).map(([key, value]) => ({
    key,
    value: formatUnknownValue(value),
  }));
}

type StatusSectionProps = {
  selectedHotelId: string | undefined;
};

export function StatusSection({ selectedHotelId }: StatusSectionProps): ReactElement {
  const {
    data: selectedHotelStatusResponse,
    error: selectedHotelStatusError,
    isError: hasSelectedHotelStatusError,
    isLoading: isSelectedHotelStatusLoading,
  } = useQuery({
    ...(selectedHotelId
      ? hotelGetStatusOptions({
          path: {
            id: selectedHotelId,
          },
        })
      : hotelGetStatusOptions({
          path: {
            id: '',
          },
        })),
    enabled: Boolean(selectedHotelId),
    staleTime: 60 * 1000,
  });
  const selectedHotelStatusRows = useMemo(
    () => buildStatusRows(selectedHotelStatusResponse),
    [selectedHotelStatusResponse]
  );
  return (
    <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-slate-200">
      {isSelectedHotelStatusLoading ? (
        <p className="px-3 py-2 text-xs text-slate-600">Loading hotel status...</p>
      ) : null}
      {hasSelectedHotelStatusError ? (
        <p className="px-3 py-2 text-xs text-rose-700">
          {getApiErrorMessage(selectedHotelStatusError, 'Unable to load hotel status.')}
        </p>
      ) : null}
      {!isSelectedHotelStatusLoading &&
      !hasSelectedHotelStatusError &&
      selectedHotelStatusResponse === undefined ? (
        <p className="px-3 py-2 text-xs text-slate-600">No status found.</p>
      ) : null}
      {selectedHotelStatusResponse !== undefined ? (
        <table className="w-full table-fixed border-collapse text-left text-sm">
          <tbody>
            {selectedHotelStatusRows.map((row) => (
              <tr key={row.id ?? row.key} className="border-b border-slate-100 last:border-b-0">
                <th className="w-40 break-words bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600 align-top">
                  {row.key}
                </th>
                <td className="break-words px-3 py-2 text-xs text-slate-800 align-top">
                  {row.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
